import {
  Box,
  Button,
  getColor,
  Icons,
  Typography,
  useToast,
  Tooltip,
} from "fds";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import config from "../../shared/config/config";
import { nanoid } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import WindowsIcon from "./logo/WindowsIcon";
import AndroidIcon from "./logo/AndroidIcon";
import MacIcon from "./logo/MacIcon";

const DownloadClientPage = ({
  changeLoading,
}: {
  changeLoading: (status: boolean) => void;
}) => {
  const GATEWAY_URL = config.servers.gateway.url;
  const toast = useToast();
  const { t } = useTranslation();
  const copy = require("clipboard-copy");
  const navigate = useNavigate();
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const [step, changeStep] = useState<"DOWNLOAD" | "COMPLETE">("DOWNLOAD");
  const [installation, setInstallation] = useState({
    allowMacOS: false,
    allowMobile: false,
  });

  useEffect(() => {
    const url = `${GATEWAY_URL}/papi/public/installation`;
    axios
      .get(url)
      .then((res) => setInstallation(res.data))
      .catch((err) => {
        console.log(err);
      });
  }, []);
  /** 윈도우 다운로드 버튼 클릭시 */
  const downloadService = (
    osName: "32" | "64" | "mac?lang=ko" | "mac?lang=en"
  ): void => {
    const URL = `${GATEWAY_URL}/papi/public/client/${osName}`;
    changeLoading(true);
    axios
      .get(URL)
      .then((answer) => {
        const newDiv = document.createElement("a");
        newDiv.href = URL;
        newDiv.click();
        changeLoading(false);
      })
      .catch((err) => {
        const axErr = err as AxiosError<any>;
        const data = axErr.response?.data;
        const msg =
          data?.errorMessage || data?.message || axErr.message || "ERROR";
        changeLoading(false);
        toast.toastMsg(nanoid(), msg, "error");
      });
  };

  const handleCopyLink = () => {
    copy(window.location.hostname);
    toast.toastMsg(nanoid(), t("file.link.copySuccess"), "success");
  };
  return window.eco.isCustomService ? (
    <div />
  ) : step === "DOWNLOAD" ? (
    <Box classes="downloadClient" direction="column">
      <img
        src={utilsCommon.renderWrapsodyECOLogo()}
        width={136}
        alt="logo"
        className="cursor pointer"
        onClick={() =>
          loginDto
            ? navigate(
                utilsCommon.renderProperNavigatePath(
                  loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                    ? `/myInbox/received`
                    : "/home"
                )
              )
            : navigate(`${process.env.PUBLIC_URL}/login`)
        }
      />
      <Typography type="h1" style={{ marginTop: 50 }}>
        {t("login.download.installEco")}
      </Typography>
      <Typography
        type="body1_md"
        exactColor={getColor("COLOR_TEXT_SECONDARY")}
        classes="mg mt-16"
      >
        {t("login.download.installEcoDesc")}
      </Typography>
      <Box direction="column" classes="downloadClient-graybox mg mt-40">
        <Typography
          type="subtitle2"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
        >
          {t("login.download.desktop")}
        </Typography>
        <Box classes="downloadClient-graybox--items" justifyContent="center">
          <Box
            classes="downloadClient-graybox--item"
            onClick={() => downloadService("64")}
            width={200}
            boxSizing="border-box"
            justifyContent="center"
          >
            <WindowsIcon />
            <Box direction="column" classes="mg ml-8">
              <Typography
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_INVERSE")}
                whiteSpace="break-spaces"
              >
                {t("login.download.windowsDownload64")}
              </Typography>
            </Box>
          </Box>
          <Box
            classes="downloadClient-graybox--item"
            onClick={() => downloadService("32")}
            width={200}
            boxSizing="border-box"
            justifyContent="center"
          >
            <WindowsIcon />
            <Box direction="column" classes="mg ml-8">
              <Typography
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_INVERSE")}
                whiteSpace="break-spaces"
              >
                {t("login.download.windowsDownload32")}
              </Typography>
            </Box>
          </Box>
        </Box>
        {installation.allowMacOS && (
          <Box classes="downloadClient-graybox--items" justifyContent="center">
            <Box
              classes="downloadClient-graybox--item"
              onClick={() => downloadService("mac?lang=ko")}
              width={200}
              boxSizing="border-box"
              justifyContent="center"
            >
              <MacIcon />
              <Box direction="column" classes="mg ml-8">
                <Typography
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_INVERSE")}
                  whiteSpace="break-spaces"
                >
                  {t("login.download.macDownloadKo")}
                </Typography>
              </Box>
            </Box>
            <Box
              classes="downloadClient-graybox--item"
              onClick={() => downloadService("mac?lang=en")}
              width={200}
              boxSizing="border-box"
              justifyContent="center"
            >
              <MacIcon />
              <Box direction="column" classes="mg ml-8">
                <Typography
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_INVERSE")}
                  whiteSpace="break-spaces"
                >
                  {t("login.download.macDownloadEn")}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Box direction="column" classes="downloadClient-graybox mg mt-12">
        <Typography
          type="subtitle2"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
        >
          {t("login.download.desktopApp")}
        </Typography>
        <Box classes="downloadClient-graybox--items" justifyContent="center">
          <Box
            classes="downloadClient-graybox--item"
            width={200}
            boxSizing="border-box"
            justifyContent="center"
          >
            <a
              style={{ textDecoration: "none" }}
              href={t("login.download.desktopAndroidAppUrl")}
            >
              <Box alignItems="center">
                <WindowsIcon />
                <Typography
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_INVERSE")}
                  classes="mg ml-8"
                  whiteSpace="pre"
                >
                  {t("login.download.windowDownloadBtn")}
                </Typography>
              </Box>
            </a>
          </Box>
          <Box
            classes="downloadClient-graybox--item"
            width={200}
            boxSizing="border-box"
            justifyContent="center"
          >
            <a
              style={{ textDecoration: "none" }}
              href={t("login.download.desktopIOSAppUrl")}
            >
              <Box alignItems="center">
                <MacIcon />
                <Typography
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_INVERSE")}
                  classes="mg ml-8"
                  whiteSpace="pre"
                >
                  {t("login.download.macDownloadBtn")}
                </Typography>
              </Box>
            </a>
          </Box>
        </Box>
      </Box>
      {installation.allowMobile && (
        <Box direction="column" classes="downloadClient-graybox mg mt-12">
          <Typography
            type="subtitle2"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {t("login.download.mobile")}
          </Typography>
          <Box classes="downloadClient-graybox--items" justifyContent="center">
            <Box direction="column" alignItems="center">
              <Box
                classes="downloadClient-graybox--item mg mb-16"
                width={200}
                boxSizing="border-box"
                justifyContent="center"
              >
                <a
                  style={{ textDecoration: "none" }}
                  href="https://play.google.com/store/apps/details?id=com.fasoo.m.wrapsodyEco"
                >
                  <Box alignItems="center">
                    <AndroidIcon />
                    <Typography
                      type="body2_rg"
                      exactColor={getColor("COLOR_TEXT_INVERSE")}
                      classes="mg ml-8"
                      whiteSpace="pre"
                    >
                      {t("login.download.andriodDownload")}
                    </Typography>
                  </Box>
                </a>
              </Box>
              <img
                src={require("../../shared/images/common/android.png")}
                alt="qr_adnroid"
                width={52}
                height={52}
              />
            </Box>

            <Box direction="column" alignItems="center">
              <Box
                classes="downloadClient-graybox--item mg mb-16"
                width={200}
                boxSizing="border-box"
                justifyContent="center"
              >
                <a
                  style={{ textDecoration: "none" }}
                  href={t("login.download.iosURL")}
                >
                  <Box>
                    <MacIcon />
                    <Typography
                      type="body2_rg"
                      exactColor={getColor("COLOR_TEXT_INVERSE")}
                      classes="mg ml-8"
                      whiteSpace="pre"
                    >
                      {t("login.download.iosDownload")}
                    </Typography>
                  </Box>
                </a>
              </Box>
              <img
                src={require("../../shared/images/common/ios.png")}
                alt="qr_adnroid"
                width={52}
                height={52}
              />
            </Box>
          </Box>
        </Box>
      )}
      <Typography
        exactColor={getColor("COLOR_TEXT_BRAND")}
        type="subtitle2"
        classes="mg mt-24"
      >
        {t("login.download.installNext")}
      </Typography>
      <Button
        label={t("common.next")}
        btnStyle="fill"
        width={"500px"}
        onClick={() => changeStep("COMPLETE")}
        classes="mg mt-24"
        style={{ height: "48px" }}
      />
    </Box>
  ) : (
    <Box classes="downloadClient" direction="column" justifyContent="center">
      <img
        src={utilsCommon.renderWrapsodyECOLogo()}
        width={136}
        alt="logo"
        className="cursor pointer"
        onClick={() =>
          loginDto
            ? navigate(
                utilsCommon.renderProperNavigatePath(
                  loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                    ? `/myInbox/received`
                    : "/home"
                )
              )
            : navigate(`${process.env.PUBLIC_URL}/login`)
        }
      />
      <img
        src={require("../../shared/images/common/check_circle.png")}
        className="mg mt-30"
        style={{ marginTop: "30px" }}
      />
      <Typography type="h1" classes="mg mt-20">
        {t("login.download.readyForEco")}
      </Typography>
      <Typography
        type="body1_rg"
        whiteSpace="break-spaces"
        textAlign="center"
        classes="mg mt-24"
        exactColor={getColor("COLOR_TEXT_SECONDARY")}
      >
        <Trans
          i18nKey="login.download.readyForEcoDesc"
          components={{
            1: (
              <Typography
                type="body1_rg"
                fontWeight={700}
                exactColor={getColor("COLOR_TEXT_BRAND")}
                classes="mg ml-4"
              />
            ),
          }}
        />
      </Typography>
      <Box direction="column" classes="downloadClient-info">
        <Box>
          <Typography whiteSpace="pre" type="body1_rg">
            {t("file.policy.siteUrl")}:
          </Typography>
          <Tooltip
            message={window.location.origin}
            classes="mg ml-4"
            width={"calc(100% - 80px)"}
          >
            <Typography ellipsis type="body1_rg">
              {window.location.origin}
            </Typography>
          </Tooltip>
        </Box>
        {window.location.port && (
          <Typography type="body1_rg">
            {t("login.download.port")}: {window.location.port}
          </Typography>
        )}
      </Box>
      <Button
        btnStyle="fill"
        label={t("login.download.copyURL")}
        classes="mg mt-24"
        onClick={handleCopyLink}
        leadingIcon={<Icons variant="line" label="copy" stroke="white" />}
      />
      <Button
        btnStyle="link"
        textColor={getColor("COLOR_TEXT_BRAND")}
        label={t("login.link.goHome")}
        style={{ marginTop: "30px" }}
        onClick={() =>
          loginDto
            ? navigate(
                utilsCommon.renderProperNavigatePath(
                  loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                    ? `/myInbox/received`
                    : "/home"
                )
              )
            : navigate(`${process.env.PUBLIC_URL}/login`)
        }
      />
    </Box>
  );
};

export default DownloadClientPage;
